import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { EntraAuthService } from 'src/app/services/entra-auth.service';
import {
  logGoogleFlow,
  logGoogleFlowRedirect,
} from 'src/app/utils/google-auth-flow-log';

/**
 * MSAL popup redirect bridge + full-page loginRedirect return (/redirect?code=).
 */
@Component({
  selector: 'app-redirect-bridge',
  template: `<p class="redirect-bridge-msg">Returning to Health Exam…</p>`,
  styles: [
    `
      .redirect-bridge-msg {
        font-family: system-ui, sans-serif;
        text-align: center;
        margin-top: 3rem;
        color: #555;
      }
    `,
  ],
})
export class RedirectBridgeComponent implements OnInit {
  constructor(
    private readonly _entraAuthService: EntraAuthService,
    private readonly _authService: AuthService
  ) {}

  ngOnInit(): void {
    const isPopupOpener =
      typeof window !== 'undefined' &&
      !!window.opener &&
      window.opener !== window;

    logGoogleFlow('redirect-bridge:ngOnInit', {
      isPopupOpener,
      queryKeys: this.queryKeys(),
    });

    if (isPopupOpener) {
      logGoogleFlow('redirect-bridge:path=popup-bridge');
      void this.completePopupRedirect();
      return;
    }

    void this.completeFullPageRedirect();
  }

  private queryKeys(): string[] {
    if (typeof window === 'undefined') {
      return [];
    }
    const params = new URLSearchParams(window.location.search);
    const keys: string[] = [];
    params.forEach((_v, k) => {
      if (!keys.includes(k)) keys.push(k);
    });
    return keys;
  }

  private async completeFullPageRedirect(): Promise<void> {
    const oauthFailure = this._entraAuthService.peekOAuthFailureInBrowser();
    const oauthCallback = this.isOAuthRedirectCallback();

    logGoogleFlow('redirect-bridge:fullPageRedirect', {
      oauthFailure,
      oauthCallback,
    });

    if (oauthFailure) {
      logGoogleFlow('redirect-bridge:path=oauthFailure → /login');
      this._entraAuthService.noteSocialRedirectAborted();
      logGoogleFlowRedirect(
        'redirect-bridge:navigate',
        '/login',
        { reason: 'oauthFailure' }
      );
      window.location.replace('/login');
      return;
    }

    if (oauthCallback) {
      logGoogleFlow('redirect-bridge:path=oauthCallback (code/state present)');
      try {
        await this._entraAuthService.awaitRedirectResponse();
        const account = await this._entraAuthService.getCurrentAccount();
        logGoogleFlow('redirect-bridge:msal handleRedirectPromise done', {
          hasAccount: !!account,
        });
        if (!account) {
          logGoogleFlow(
            'redirect-bridge:path=oauthCallbackNoAccount → /login (retry)'
          );
          this._entraAuthService.noteSocialRedirectAborted();
          logGoogleFlowRedirect('redirect-bridge:navigate', '/login', {
            reason: 'oauthCallbackNoAccount',
          });
          window.location.replace('/login');
          return;
        }
        this._authService.markSocialOAuthReturn();
        this._authService.clearSocialRedirectAttemptWindow();
        this._entraAuthService.clearSocialOAuthAbortedFlag();
        this._entraAuthService.stripOAuthParamsFromBrowserUrl();
        logGoogleFlowRedirect('redirect-bridge:navigate', '/login', {
          reason: 'oauthSuccess',
          he_social_oauth_return: '1',
        });
        window.location.replace('/login');
        return;
      } catch (err) {
        console.error('[auth] Full-page OAuth redirect handling failed', err);
        logGoogleFlow('redirect-bridge:path=oauthCallbackError', { err });
        this._entraAuthService.noteSocialRedirectAborted();
        logGoogleFlowRedirect('redirect-bridge:navigate', '/login', {
          reason: 'oauthCallbackError',
        });
        window.location.replace('/login');
        return;
      }
    }

    logGoogleFlow('redirect-bridge:path=noOAuthParams → /login');
    this._entraAuthService.noteSocialRedirectAborted();
    logGoogleFlowRedirect('redirect-bridge:navigate', '/login', {
      reason: 'noOAuthParams',
    });
    window.location.replace('/login');
  }

  private isOAuthRedirectCallback(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('code') || params.get('state')) {
      return true;
    }
    const hash = window.location.hash || '';
    return /(^|[?&])(code|state)=/i.test(hash);
  }

  private async completePopupRedirect(): Promise<void> {
    logGoogleFlow('redirect-bridge:popup broadcastResponseToMainFrame');
    try {
      const { broadcastResponseToMainFrame } = await import(
        '@azure/msal-browser/redirect-bridge'
      );
      await broadcastResponseToMainFrame();
      logGoogleFlow('redirect-bridge:popup broadcast done');
    } catch (error) {
      console.error('Error broadcasting response to main frame:', error);
      logGoogleFlow('redirect-bridge:popup broadcast failed', { error });
    }
  }
}
